import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { enterUpDelayed } from '../../utils/animations';

const intro = 'These Terms of Service ("Terms") govern your use of whiteglovehiring.com (the "Site"), operated by White Glove Hiring, LLC, a Washington limited liability company ("White Glove Hiring," "we," "us," or "our"). By using the Site, booking a call, or contacting us through the Site, you agree to these Terms. You must be at least 18 years old to use the Site. If you do not agree to these Terms, please do not use the Site.';

const sections = [
    {
        title: '1. Our Services',
        blocks: [
            { type: 'p', text: 'White Glove Hiring helps businesses hire, onboard, and manage virtual assistants. We are not an employer of record, staffing agency, leasing company, or payroll provider. Virtual assistants placed through our program are independent contractors engaged and paid directly by our clients.' },
        ],
    },
    {
        title: '2. Signed Agreements Control',
        blocks: [
            { type: 'p', text: 'Using the Site or booking a call does not create a client relationship. Our services are provided only under a signed Client Services Agreement, and our referral program operates only under a signed Referral Partner Agreement. If anything on the Site or in these Terms conflicts with a signed agreement, the signed agreement controls.' },
        ],
    },
    {
        title: '3. Pricing and Cost Examples',
        blocks: [
            { type: 'p', text: 'Pricing shown on the Site is provided for general information. Cost examples, monthly totals, and hourly figures are estimates based on stated assumptions, such as a sample virtual assistant wage and number of monthly hours. Actual virtual assistant wages vary by skill, experience, and role, and are paid directly by the client to the virtual assistant. Your pricing is set by your signed agreement. We may change the pricing shown on the Site at any time. Changes to Site pricing do not change the terms of a signed agreement.' },
        ],
    },
    {
        title: '4. Diagnostic Calls',
        blocks: [
            { type: 'p', text: 'Diagnostic calls carry no obligation to purchase. Anything we share during a call is general guidance about our services, not legal, tax, employment, or financial advice.' },
        ],
    },
    {
        title: '5. No Guarantee of Results',
        blocks: [
            { type: 'p', text: "We work hard to place strong candidates and support long-term success, but we do not guarantee any business result, candidate performance, or specific outcome. Results described on the Site, including our founder's own business results, are examples from specific situations. They are not typical or guaranteed. Your results depend on many factors, including your business, your systems, and how you work with your virtual assistant. Any guarantee we offer, such as our replacement guarantee, applies only as described in your signed agreement." },
        ],
    },
    {
        title: '6. Not Professional Advice',
        blocks: [
            { type: 'p', text: 'Content on the Site, including articles, guides, and examples, is for general information only. It is not legal, tax, accounting, employment, or financial advice. Please consult a qualified professional about your specific situation.' },
        ],
    },
    {
        title: '7. Virtual Assistant Candidates',
        blocks: [
            { type: 'p', text: 'Submitting an application or information to us does not guarantee an interview, placement, or engagement, and does not create an employment relationship with White Glove Hiring. Candidates who are placed are engaged directly by our clients as independent contractors, under terms agreed between the candidate and the client.' },
        ],
    },
    {
        title: '8. SMS Text Messaging Program',
        blocks: [
            { type: 'p', text: 'White Glove Hiring sends text messages only to people who provide their phone number and agree to receive texts, such as clients, prospective clients who book a call, and candidates.' },
            { type: 'ul', items: [
                'Messages relate to scheduled calls, interviews, onboarding, and our services.',
                'Message frequency varies.',
                'Message and data rates may apply.',
                'Reply STOP at any time to cancel. You will receive one confirmation message and no further texts.',
                'Reply HELP for help, or email adam@whiteglovehiring.com.',
                'Consent to receive text messages is not a condition of purchasing any service.',
                'Carriers are not liable for delayed or undelivered messages.',
            ] },
            { type: 'plink', pre: 'Our ', post: ' explains how we handle your information and confirms that we do not share mobile opt-in data or consent with third parties.' },
        ],
    },
    {
        title: '9. Acceptable Use',
        blocks: [
            { type: 'p', text: 'You agree not to:' },
            { type: 'ul', items: [
                'Provide false or misleading information',
                'Use the Site for any unlawful purpose',
                'Copy, scrape, or harvest content or data from the Site using automated means',
                "Interfere with the Site's security or operation",
                'Upload viruses or other harmful code',
                'Impersonate any person or business',
            ] },
        ],
    },
    {
        title: '10. Intellectual Property',
        blocks: [
            { type: 'p', text: 'The Site and its content, including text, graphics, logos, the White Glove Hiring name, and our hiring frameworks, processes, and materials, are owned by White Glove Hiring and protected by law. You may view the Site for personal or internal business purposes. You may not copy, reproduce, distribute, or create derivative works from our content without our written permission.' },
        ],
    },
    {
        title: '11. Third-Party Tools and Links',
        blocks: [
            { type: 'p', text: 'The Site includes tools and links operated by third parties, such as scheduling calendars, video players, and video conferencing services. We are not responsible for their content, availability, or practices, and your use of them is subject to their own terms.' },
        ],
    },
    {
        title: '12. Accuracy of Information',
        blocks: [
            { type: 'p', text: 'We work to keep the Site accurate and current, but it may contain errors, omissions, or outdated information. We may correct errors and update content at any time without notice.' },
        ],
    },
    {
        title: '13. Disclaimer of Warranties',
        blocks: [
            { type: 'p', text: 'The Site is provided "as is" and "as available," without warranties of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Site will be uninterrupted, secure, or error-free.' },
        ],
    },
    {
        title: '14. Limitation of Liability',
        blocks: [
            { type: 'p', text: 'To the fullest extent permitted by law, White Glove Hiring will not be liable for any indirect, incidental, consequential, special, or punitive damages arising from your use of the Site. Our total liability for any claim related to your use of the Site will not exceed one hundred dollars ($100). This section applies to use of the Site only. Liability related to our services is governed by your signed agreement.' },
        ],
    },
    {
        title: '15. Indemnification',
        blocks: [
            { type: 'p', text: "You agree to indemnify and hold harmless White Glove Hiring and its owners, team members, and contractors from any claims, losses, or expenses, including reasonable attorneys' fees, arising from your misuse of the Site or your violation of these Terms." },
        ],
    },
    {
        title: '16. Governing Law and Disputes',
        blocks: [
            { type: 'p', text: 'These Terms are governed by the laws of the State of Washington, without regard to conflict of law rules. Any dispute arising from these Terms or your use of the Site will be resolved in the state or federal courts located in King County, Washington. Disputes arising under a signed agreement are resolved as that agreement provides.' },
        ],
    },
    {
        title: '17. Changes to These Terms',
        blocks: [
            { type: 'p', text: 'We may update these Terms from time to time. Changes will be posted on this page with an updated date. Continued use of the Site after changes are posted means you accept the updated Terms.' },
        ],
    },
    {
        title: '18. General',
        blocks: [
            { type: 'plink', pre: 'If any part of these Terms is found unenforceable, the rest will remain in effect. Our failure to enforce any part of these Terms is not a waiver of our rights. These Terms, together with our ', post: ', are the entire agreement between you and us regarding your use of the Site.' },
        ],
    },
];

function TermsBlock({ block }) {
    if (block.type === 'ul') {
        return (
            <ul className="list-disc pl-6 mb-4">
                {block.items.map((item, i) => (
                    <li key={i} className="text-[#6B7280] leading-relaxed mb-2 last:mb-0">{item}</li>
                ))}
            </ul>
        );
    }
    if (block.type === 'plink') {
        return (
            <p className="text-[#6B7280] leading-relaxed mb-4 last:mb-0">
                {block.pre}
                <Link to="/privacy-policy" className="text-navy-900 font-semibold underline underline-offset-2 hover:text-gold-500 transition-colors">Privacy Policy</Link>
                {block.post}
            </p>
        );
    }
    return <p className="text-[#6B7280] leading-relaxed mb-4 last:mb-0">{block.text}</p>;
}

export function TermsContent() {
    return (
        <section className="bg-white py-24 md:py-32">
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
                <motion.div {...enterUpDelayed(0.1)} data-testid="terms-content">
                    <p className="text-[#6B7280] leading-relaxed mb-10">{intro}</p>
                    {sections.map((section) => (
                        <div key={section.title} className="mb-10">
                            <h2 className="font-montserrat font-bold text-xl text-navy-900 mb-3">{section.title}</h2>
                            {section.blocks.map((block, i) => (
                                <TermsBlock key={i} block={block} />
                            ))}
                        </div>
                    ))}
                </motion.div>

                <div className="mt-12 pt-8 border-t border-gray-100">
                    <Link to="/contact" className="text-navy-900 text-sm font-semibold hover:text-gold-400 transition-colors" data-testid="terms-contact-link">
                        Questions? Contact us
                    </Link>
                </div>
            </div>
        </section>
    );
}
